import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Landmark, Plus, Pencil, Trash2, X, TrendingDown, TrendingUp,
  AlertTriangle, Clock, IndianRupee, History, Receipt, CheckCircle2,
  Brain, Scale, BarChart3,
} from 'lucide-react';
import DataGrid from '../components/DataGrid';
import { DrillCell } from '../components/DrillPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = {
  lender_name: '', lender_type: 'Private', loan_date: today(),
  principal_amount: '', interest_rate: '', interest_period: 'annual',
  interest_method: 'simple', tenure_months: '',
  purpose: 'Raw Materials', notes: '',
  next_payment_date: '', next_payment_amount: '',
  lender_contact: '',
};

const STATUS_STYLE = {
  Active:  'bg-amber-900/30 text-amber-300 border-amber-700',
  Settled: 'bg-green-900/40 text-green-300 border-green-700',
  Closed:  'bg-zinc-800 text-zinc-300 border-zinc-700',
};

/**
 * Loans — Phase A of the Loan & Capital ROI Engine.
 *
 *   • Top: Upcoming Payments + Summary rollup cards
 *   • Below: DataGrid of all loans with sort/filter/Excel/CSV (BI Phase 1)
 *   • Modals: Add Loan, Edit Loan (with audit log), Record Payment, Detail (with payments)
 *   • All cashflow sync happens server-side (loans.py)
 */
export default function Loans() {
  const [loans, setLoans]   = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('list');  // iter176 — list | roi

  const [showAdd, setShowAdd]       = useState(false);
  const [editLoan, setEditLoan]     = useState(null);
  const [detailLoan, setDetailLoan] = useState(null);
  const [payFor, setPayFor]         = useState(null);
  const [settleFor, setSettleFor]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/loans/`, { withCredentials: true }),
        axios.get(`${API}/loans/summary`, { withCredentials: true }),
      ]);
      setLoans(a.data || []);
      setSummary(b.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load loans');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-4" data-testid="loans-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Landmark className="text-amber-400" size={22}/>
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Loans & Liabilities</h1>
            <p className="text-zinc-500 text-sm">Unified registry · Bank & Private · auto-syncs with Cashflow</p>
          </div>
        </div>
        <button onClick={() => setShowAdd(true)}
          data-testid="add-loan-btn"
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 rounded-lg text-sm font-semibold">
          <Plus size={14}/> Add Loan
        </button>
      </div>

      {/* iter176 — Tabs */}
      <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 w-fit">
        <button onClick={() => setTab('list')} data-testid="tab-list"
          className={`text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 ${tab === 'list' ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}>
          <Landmark size={11}/> Registry
        </button>
        <button onClick={() => setTab('roi')} data-testid="tab-roi"
          className={`text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 ${tab === 'roi' ? 'bg-emerald-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}>
          <Brain size={11}/> ROI · Is it Worth It?
        </button>
      </div>

      {tab === 'roi' && <RoiPanel/>}

      {tab === 'list' ? <>
      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <SummaryCard icon={<IndianRupee size={13} className="text-amber-400"/>} label="Borrowed" val={fmt(summary.total_borrowed)} testId="sum-borrowed" />
          <SummaryCard icon={<TrendingDown size={13} className="text-red-400"/>} label="Outstanding" val={fmt(summary.total_outstanding)} testId="sum-outstanding" />
          <SummaryCard icon={<Receipt size={13} className="text-violet-400"/>} label="Interest Paid" val={fmt(summary.total_interest_paid)} testId="sum-interest" />
          <SummaryCard icon={<Clock size={13} className="text-amber-400"/>} label="Active Loans" val={summary.active_count} testId="sum-active" />
          <SummaryCard icon={<CheckCircle2 size={13} className="text-green-400"/>} label="Settled" val={summary.settled_count} testId="sum-settled" />
          <SummaryCard icon={<AlertTriangle size={13} className="text-red-400"/>} label="Overdue" val={summary.overdue_count} hot={summary.overdue_count > 0} testId="sum-overdue" />
        </div>
      )}

      {/* Upcoming Payments card (top) */}
      {summary && summary.upcoming_30d.length > 0 && (
        <div className="rounded-xl border border-amber-700/40 bg-gradient-to-br from-amber-950/30 to-zinc-900 p-4" data-testid="upcoming-payments-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="text-amber-400" size={14}/>
              <span className="text-zinc-100 text-sm font-semibold">Upcoming Payments (next 30 days)</span>
              <span className="text-[10px] text-amber-300 bg-amber-900/40 px-1.5 py-0.5 rounded">
                {summary.upcoming_30d.length}
              </span>
            </div>
            <div className="text-[10px] text-zinc-500">
              Within 7d: <span className="text-amber-300 font-semibold">{summary.upcoming_7d.length}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {summary.upcoming_30d.slice(0, 9).map((u) => (
              <div key={u.loan_id} onClick={() => setDetailLoan(loans.find(l => l.id === u.loan_id))}
                data-testid={`upcoming-${u.loan_id}`}
                className={`rounded-md border px-3 py-2 cursor-pointer hover:bg-zinc-800/40 ${u.days <= 7 ? 'border-amber-700/60 bg-amber-950/30' : 'border-zinc-800 bg-zinc-950/60'}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-zinc-100 text-xs font-semibold truncate max-w-[160px]">{u.lender}</span>
                  <span className={`text-[10px] font-mono ${u.days <= 0 ? 'text-red-400 font-bold' : u.days <= 3 ? 'text-amber-300 font-bold' : u.days <= 7 ? 'text-amber-400' : 'text-zinc-400'}`}>
                    {u.days < 0 ? `${Math.abs(u.days)}d late` : u.days === 0 ? 'today' : `in ${u.days}d`}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-zinc-500 font-mono">{u.due_date}</span>
                  <span className="text-xs text-emerald-300 font-mono font-bold">{u.amount_due > 0 ? fmt(u.amount_due) : '—'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loans Grid */}
      <DataGrid
        testId="loans-grid"
        title="All Loans"
        subtitle="Sort, filter, export — click any loan name to drill in"
        icon={<Landmark size={14} className="text-amber-400" />}
        rows={loans}
        rowKey={(l) => l.id}
        initialSort={{ key: 'loan_date', dir: 'desc' }}
        exportName="loans"
        emptyText={loading ? 'Loading loans…' : 'No loans yet. Click "Add Loan" to start.'}
        columns={[
          { key: 'lender_name', label: 'Lender', type: 'text',
            accessor: l => l.lender_name,
            render: l => (
              <button onClick={() => setDetailLoan(l)} data-testid={`open-loan-${l.id}`}
                className="text-left hover:underline decoration-amber-500 decoration-dotted underline-offset-2">
                <div className="text-zinc-100 font-medium text-xs">{l.lender_name}</div>
                <div className="text-[10px] text-zinc-500">{l.purpose || '—'}</div>
              </button>
            ) },
          { key: 'lender_type', label: 'Type', type: 'enum',
            accessor: l => l.lender_type,
            render: l => <span className={`text-[10px] px-1.5 py-0.5 rounded border ${l.lender_type === 'Bank' ? 'bg-blue-900/30 text-blue-300 border-blue-700' : 'bg-violet-900/30 text-violet-300 border-violet-700'}`}>{l.lender_type}</span> },
          { key: 'loan_date', label: 'Date', type: 'date',
            accessor: l => l.loan_date,
            render: l => <span className="font-mono text-zinc-400 text-xs">{l.loan_date}</span> },
          { key: 'principal_amount', label: 'Principal', type: 'number', align: 'right',
            accessor: l => l.principal_amount,
            render: l => <span className="font-mono text-zinc-200 text-xs">{fmt(l.principal_amount)}</span> },
          { key: 'annualized_rate', label: 'Rate', type: 'number', align: 'right',
            accessor: l => l.annualized_rate,
            render: l => <span className="font-mono text-amber-300 text-xs">{l.annualized_rate}%</span> },
          { key: 'outstanding_principal', label: 'Outstanding', type: 'number', align: 'right',
            accessor: l => l.outstanding_principal,
            render: l => <span className={`font-mono text-xs font-bold ${l.outstanding_principal > 0 ? 'text-red-300' : 'text-green-400'}`}>{fmt(l.outstanding_principal)}</span> },
          { key: 'accrued_interest', label: 'Accrued', type: 'number', align: 'right',
            accessor: l => l.accrued_interest,
            render: l => l.accrued_interest > 0
              ? <span className="font-mono text-amber-400 text-xs">{fmt(l.accrued_interest)}</span>
              : <span className="text-zinc-700 text-xs">—</span> },
          { key: 'total_interest_paid', label: 'Int. Paid', type: 'number', align: 'right',
            accessor: l => l.total_interest_paid,
            render: l => <span className="font-mono text-violet-300 text-xs">{fmt(l.total_interest_paid)}</span> },
          { key: 'next_payment_date', label: 'Next Due', type: 'date',
            accessor: l => l.next_payment_date || '',
            render: l => {
              if (!l.next_payment_date) return <span className="text-zinc-700 text-xs">—</span>;
              const days = Math.floor((new Date(l.next_payment_date) - new Date()) / 86400000);
              return (
                <div>
                  <div className="font-mono text-zinc-300 text-xs">{l.next_payment_date}</div>
                  <div className={`text-[9px] ${days < 0 ? 'text-red-400 font-bold' : days <= 7 ? 'text-amber-300' : 'text-zinc-500'}`}>
                    {days < 0 ? `${Math.abs(days)}d late` : days === 0 ? 'today' : `in ${days}d`}
                  </div>
                </div>
              );
            } },
          { key: 'status', label: 'Status', type: 'enum',
            accessor: l => l.status,
            render: l => <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLE[l.status] || STATUS_STYLE.Active}`}>{l.status}</span> },
          { key: 'actions', label: '', sortable: false, filterable: false, exportable: false,
            accessor: () => '',
            render: l => (
              <div className="flex items-center gap-1">
                <button onClick={() => setPayFor(l)} data-testid={`pay-${l.id}`}
                  disabled={l.status !== 'Active'}
                  title={l.status !== 'Active' ? 'Loan is not active' : 'Record payment'}
                  className="p-1 rounded text-emerald-400 hover:text-emerald-300 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed">
                  <IndianRupee size={11}/>
                </button>
                <button onClick={() => setSettleFor(l)} data-testid={`settle-${l.id}`}
                  disabled={l.status !== 'Active'}
                  title={l.status !== 'Active' ? 'Loan already settled' : 'Foreclose / settle loan early'}
                  className="p-1 rounded text-purple-400 hover:text-purple-300 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed">
                  <CheckCircle2 size={11}/>
                </button>
                <button onClick={() => setEditLoan(l)} data-testid={`edit-${l.id}`}
                  className="p-1 rounded text-zinc-400 hover:text-amber-400 hover:bg-zinc-800">
                  <Pencil size={11}/>
                </button>
              </div>
            ) },
        ]}
      />
      </> : null}

      {showAdd && <LoanModal mode="add" form={EMPTY_FORM} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editLoan && <LoanModal mode="edit" form={editLoan} onClose={() => setEditLoan(null)} onSaved={() => { setEditLoan(null); load(); }} />}
      {payFor && <PaymentModal loan={payFor} onClose={() => setPayFor(null)} onSaved={() => { setPayFor(null); load(); }} />}
      {settleFor && <SettlementModal loan={settleFor} onClose={() => setSettleFor(null)} onSaved={() => { setSettleFor(null); load(); }} />}
      {detailLoan && <LoanDetail loan={detailLoan} onClose={() => setDetailLoan(null)} onRecordPayment={() => { setPayFor(detailLoan); setDetailLoan(null); }} onEdit={() => { setEditLoan(detailLoan); setDetailLoan(null); }} onChanged={load} />}
    </div>
  );
}

function SummaryCard({ icon, label, val, hot, testId }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${hot ? 'border-red-700/60 bg-red-950/20' : 'border-zinc-800 bg-zinc-900/60'}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">{icon}{label}</div>
      <div className={`text-base font-bold mt-0.5 ${hot ? 'text-red-300' : 'text-zinc-100'}`}>{val}</div>
    </div>
  );
}

// ── Add / Edit Loan modal ──────────────────────────────────────────────
function LoanModal({ mode, form, onClose, onSaved }) {
  const [f, setF] = useState(form);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'add') {
        await axios.post(`${API}/loans/`, f, { withCredentials: true });
        toast.success('Loan added');
      } else {
        await axios.put(`${API}/loans/${f.id}`, f, { withCredentials: true });
        toast.success('Loan updated · change logged');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete loan from ${f.lender_name}? This cascades to all payments and cashflow entries.`)) return;
    try {
      await axios.delete(`${API}/loans/${f.id}`, { withCredentials: true });
      toast.success('Loan deleted');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <Modal title={mode === 'add' ? 'Add Loan' : `Edit · ${f.lender_name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="loan-form">
        <Row>
          <Field label="Lender Name *">
            <input required value={f.lender_name} onChange={e => setF({ ...f, lender_name: e.target.value })}
              data-testid="lf-lender" className={inputCls} />
          </Field>
          <Field label="Lender Type">
            <select value={f.lender_type} onChange={e => setF({ ...f, lender_type: e.target.value })} className={inputCls}>
              <option value="Private">Private</option>
              <option value="Bank">Bank</option>
              <option value="NBFC">NBFC</option>
              <option value="Family">Family</option>
            </select>
          </Field>
        </Row>
        <Row>
          <Field label="Loan Date *">
            <input required type="date" value={f.loan_date} onChange={e => setF({ ...f, loan_date: e.target.value })}
              data-testid="lf-date" className={inputCls}/>
          </Field>
          <Field label="Principal Amount (₹) *">
            <input required type="number" step="any" value={f.principal_amount}
              onChange={e => setF({ ...f, principal_amount: e.target.value })}
              data-testid="lf-principal" className={inputCls}/>
          </Field>
        </Row>
        <Row>
          <Field label="Interest Rate (%) *">
            <input required type="number" step="any" value={f.interest_rate}
              onChange={e => setF({ ...f, interest_rate: e.target.value })}
              data-testid="lf-rate" className={inputCls}/>
          </Field>
          <Field label="Rate Period">
            <select value={f.interest_period} onChange={e => setF({ ...f, interest_period: e.target.value })} className={inputCls}>
              <option value="annual">Annual (per year)</option>
              <option value="monthly">Monthly (per month)</option>
            </select>
          </Field>
        </Row>
        <Row>
          <Field label="Interest Method">
            <select value={f.interest_method} onChange={e => setF({ ...f, interest_method: e.target.value })} className={inputCls}>
              <option value="simple">Simple Interest</option>
              <option value="reducing">Reducing Balance</option>
            </select>
          </Field>
          <Field label="Tenure (months)">
            <input type="number" value={f.tenure_months}
              onChange={e => setF({ ...f, tenure_months: e.target.value })} className={inputCls}/>
          </Field>
        </Row>
        <Row>
          <Field label="Purpose">
            <select value={f.purpose} onChange={e => setF({ ...f, purpose: e.target.value })} className={inputCls}>
              {['Raw Materials', 'Machinery', 'Working Capital', 'Expansion', 'Salaries', 'Other'].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Lender Contact">
            <input value={f.lender_contact || ''} onChange={e => setF({ ...f, lender_contact: e.target.value })}
              placeholder="Phone or email" className={inputCls}/>
          </Field>
        </Row>
        <Row>
          <Field label="Next Payment Date">
            <input type="date" value={f.next_payment_date || ''}
              onChange={e => setF({ ...f, next_payment_date: e.target.value })}
              data-testid="lf-next-date" className={inputCls}/>
          </Field>
          <Field label="Next Payment Amount (₹)">
            <input type="number" value={f.next_payment_amount || ''}
              onChange={e => setF({ ...f, next_payment_amount: e.target.value })}
              data-testid="lf-next-amount" className={inputCls}/>
          </Field>
        </Row>
        {mode === 'edit' && (
          <Field label="Status">
            <select value={f.status || 'Active'} onChange={e => setF({ ...f, status: e.target.value })} className={inputCls}>
              <option value="Active">Active</option>
              <option value="Settled">Settled</option>
              <option value="Closed">Closed</option>
            </select>
          </Field>
        )}
        <Field label="Notes">
          <textarea value={f.notes || ''} onChange={e => setF({ ...f, notes: e.target.value })}
            rows={2} className={inputCls}/>
        </Field>
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-zinc-800">
          {mode === 'edit' ? (
            <button type="button" onClick={onDelete}
              className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300 px-3 py-1.5">
              <Trash2 size={11}/> Delete loan
            </button>
          ) : <div/>}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-100">Cancel</button>
            <button type="submit" disabled={busy} data-testid="lf-save"
              className="bg-amber-500 hover:bg-amber-400 text-black px-4 py-1.5 rounded text-xs font-semibold">
              {busy ? 'Saving…' : (mode === 'add' ? 'Add Loan' : 'Save Changes')}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ── Record Payment modal ──────────────────────────────────────────────
function PaymentModal({ loan, onClose, onSaved }) {
  const [date, setDate]    = useState(today());
  const [amount, setAmount]= useState('');
  const [pri, setPri]      = useState('');
  const [intr, setIntr]    = useState('');
  const [method, setMethod]= useState('Cash');
  const [notes, setNotes]  = useState('');
  const [busy, setBusy]    = useState(false);
  const [autoSplit, setAutoSplit] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { payment_date: date, payment_method: method, notes };
      if (autoSplit) {
        payload.amount_paid = parseFloat(amount) || 0;
      } else {
        payload.principal_portion = parseFloat(pri) || 0;
        payload.interest_portion  = parseFloat(intr) || 0;
      }
      const r = await axios.post(`${API}/loans/${loan.id}/payments`, payload, { withCredentials: true });
      const p = r.data.payment;
      toast.success(`Payment recorded · ₹${p.principal_portion} principal + ₹${p.interest_portion} interest`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment failed'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Record payment · ${loan.lender_name}`} onClose={onClose}>
      <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
        <SummaryCard label="Outstanding" val={fmt(loan.outstanding_principal)} testId="pm-out"/>
        <SummaryCard label="Accrued Int." val={fmt(loan.accrued_interest)} testId="pm-acc"/>
        <SummaryCard label="Rate" val={`${loan.annualized_rate}% pa`} testId="pm-rate"/>
      </div>
      <form onSubmit={submit} className="space-y-3" data-testid="payment-form">
        <Row>
          <Field label="Payment Date *"><input required type="date" value={date} onChange={e => setDate(e.target.value)} className={inputCls}/></Field>
          <Field label="Method">
            <select value={method} onChange={e => setMethod(e.target.value)} className={inputCls}>
              {['Cash', 'Bank Transfer', 'Cheque', 'UPI', 'Other'].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
        </Row>
        <label className="flex items-center gap-2 text-xs text-zinc-400">
          <input type="checkbox" checked={autoSplit} onChange={e => setAutoSplit(e.target.checked)} className="accent-amber-500"/>
          Auto-split (pay accrued interest first, balance to principal)
        </label>
        {autoSplit ? (
          <Field label="Amount Paid (₹) *">
            <input required type="number" step="any" value={amount} onChange={e => setAmount(e.target.value)}
              data-testid="pm-amount" className={inputCls}/>
          </Field>
        ) : (
          <Row>
            <Field label="Principal portion (₹)">
              <input type="number" step="any" value={pri} onChange={e => setPri(e.target.value)} className={inputCls}/>
            </Field>
            <Field label="Interest portion (₹)">
              <input type="number" step="any" value={intr} onChange={e => setIntr(e.target.value)} className={inputCls}/>
            </Field>
          </Row>
        )}
        <Field label="Notes">
          <input value={notes} onChange={e => setNotes(e.target.value)} className={inputCls}/>
        </Field>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-100">Cancel</button>
          <button type="submit" disabled={busy} data-testid="pm-save"
            className="bg-emerald-500 hover:bg-emerald-400 text-black px-4 py-1.5 rounded text-xs font-semibold">
            {busy ? 'Saving…' : 'Record Payment'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Loan Detail modal ─────────────────────────────────────────────────
function LoanDetail({ loan, onClose, onRecordPayment, onEdit, onChanged }) {
  const [d, setD] = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/loans/${loan.id}`, { withCredentials: true });
      setD(r.data);
    } catch {/* */}
  }, [loan.id]);
  useEffect(() => { reload(); }, [reload]);

  const deletePayment = async (pid) => {
    if (!window.confirm('Delete this payment? Cashflow row will be reversed.')) return;
    try {
      await axios.delete(`${API}/loans/${loan.id}/payments/${pid}`, { withCredentials: true });
      toast.success('Payment deleted');
      reload(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!d) return <Modal title="Loading…" onClose={onClose}><div className="text-zinc-500 text-sm py-8 text-center">Loading details…</div></Modal>;

  return (
    <Modal title={`Loan · ${d.lender_name}`} onClose={onClose} wide>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <SummaryCard label="Principal"   val={fmt(d.principal_amount)} testId="ld-principal"/>
        <SummaryCard label="Outstanding" val={fmt(d.outstanding_principal)} testId="ld-outstanding" hot={d.outstanding_principal > 0}/>
        <SummaryCard label="Int. Paid"   val={fmt(d.total_interest_paid)} testId="ld-int-paid"/>
        <SummaryCard label="Accrued"     val={fmt(d.accrued_interest)} testId="ld-accrued"/>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <button onClick={onRecordPayment} disabled={d.status !== 'Active'}
          data-testid="ld-record-payment"
          className="inline-flex items-center gap-1 text-xs bg-emerald-500 hover:bg-emerald-400 text-black px-3 py-1.5 rounded font-semibold disabled:opacity-40">
          <IndianRupee size={11}/> Record Payment
        </button>
        <button onClick={onEdit} className="inline-flex items-center gap-1 text-xs border border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-amber-500 px-3 py-1.5 rounded">
          <Pencil size={11}/> Edit
        </button>
        <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_STYLE[d.status] || STATUS_STYLE.Active}`}>{d.status}</span>
        <span className="text-xs text-zinc-500">{d.lender_type} · {d.purpose} · {d.annualized_rate}% pa</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Payments */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-zinc-800 text-zinc-300 text-xs font-semibold flex items-center gap-1.5">
            <Receipt size={11}/> Payments ({d.payments.length})
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-800/40 text-zinc-500 text-[10px] uppercase">
                <tr><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-right">Principal</th><th className="px-2 py-1.5 text-right">Interest</th><th className="px-2 py-1.5 text-left">Method</th><th></th></tr>
              </thead>
              <tbody>
                {d.payments.length === 0 ? (
                  <tr><td colSpan={5} className="py-4 text-center text-zinc-600">No payments yet</td></tr>
                ) : d.payments.slice().reverse().map(p => (
                  <tr key={p.id} className="border-b border-zinc-800/40">
                    <td className="px-2 py-1.5 font-mono text-zinc-300">{p.payment_date}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-amber-300">{fmt(p.principal_portion)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-violet-300">{fmt(p.interest_portion)}</td>
                    <td className="px-2 py-1.5 text-zinc-400">{p.payment_method}</td>
                    <td className="px-2 py-1.5 text-right">
                      <button onClick={() => deletePayment(p.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={10}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Audit Log */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-zinc-800 text-zinc-300 text-xs font-semibold flex items-center gap-1.5">
            <History size={11}/> Change Log ({d.audit_log.length})
          </div>
          <div className="max-h-72 overflow-y-auto p-2 space-y-2 text-xs">
            {d.audit_log.length === 0 ? (
              <div className="text-zinc-600 text-center py-4">No changes yet</div>
            ) : d.audit_log.slice().reverse().map((a, i) => (
              <div key={i} className="bg-zinc-950 border border-zinc-800 rounded p-2" data-testid={`audit-${i}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-amber-400 font-mono text-[10px]">{(a.ts || '').slice(0, 19).replace('T', ' ')}</span>
                  <span className="text-zinc-500 text-[10px]">by {a.user}</span>
                </div>
                <div className="text-zinc-300 text-[11px]">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] mr-1 ${a.action === 'created' ? 'bg-green-900/40 text-green-300' : 'bg-amber-900/30 text-amber-300'}`}>{a.action}</span>
                  {Array.isArray(a.details)
                    ? a.details.map((c, j) => (
                        <div key={j} className="ml-1 text-[10px]">
                          <span className="text-zinc-400">{c.field}:</span>{' '}
                          <span className="text-red-300 line-through">{String(c.old ?? '—')}</span>{' '}
                          → <span className="text-green-300">{String(c.new ?? '—')}</span>
                        </div>
                      ))
                    : <span className="text-zinc-400 text-[10px]">{JSON.stringify(a.details)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {d.notes && (
        <div className="mt-3 bg-zinc-950 border border-zinc-800 rounded p-2 text-xs text-zinc-400">
          <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Notes</span>
          <div>{d.notes}</div>
        </div>
      )}
    </Modal>
  );
}

// ── Small inline UI helpers ────────────────────────────────────────────
const inputCls = "w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 text-xs focus:outline-none focus:border-amber-500";
function Row({ children }) { return <div className="grid grid-cols-2 gap-2">{children}</div>; }
function Field({ label, children }) {
  return <div><label className="text-zinc-400 text-[10px] mb-0.5 block">{label}</label>{children}</div>;
}
function Modal({ title, onClose, wide, children }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className={`bg-zinc-950 border border-zinc-800 rounded-xl ${wide ? 'max-w-3xl' : 'max-w-xl'} w-full max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-950 z-10">
          <h3 className="text-zinc-100 text-sm font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 rounded text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"><X size={14}/></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
//  iter176 — Phase C ROI panel
// ─────────────────────────────────────────────────────────────────────────
const CLASS_STYLE = {
  'Net Gain':         { bg: 'bg-green-950/40 border-green-700', text: 'text-green-300', dot: 'bg-green-500' },
  'Break-even':       { bg: 'bg-amber-950/40 border-amber-700', text: 'text-amber-300', dot: 'bg-amber-500' },
  'High-Cost Burden': { bg: 'bg-red-950/40 border-red-700',     text: 'text-red-300',   dot: 'bg-red-500' },
};

const HEALTH_STYLE = {
  'Healthy':                    'text-green-400',
  'Acceptable':                 'text-amber-400',
  'Stretched':                  'text-orange-400',
  'High leverage — risk':       'text-red-400',
};

function RoiPanel() {
  const [roi, setRoi] = useState(null);
  const [de, setDe]   = useState(null);
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buffer, setBuffer]   = useState(5);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        axios.get(`${API}/loans/roi/analysis?gain_buffer_pct=${buffer}`, { withCredentials: true }),
        axios.get(`${API}/loans/roi/debt-equity`, { withCredentials: true }),
        axios.get(`${API}/loans/pnl-impact`, { withCredentials: true }),
      ]);
      setRoi(a.data); setDe(b.data); setPnl(c.data);
    } catch (e) { toast.error('Failed to load ROI'); }
    finally { setLoading(false); }
  }, [buffer]);
  useEffect(() => { load(); }, [load]);

  if (loading || !roi) return <div className="text-zinc-500 text-sm py-8 text-center">Computing ROI analysis…</div>;
  const p = roi.portfolio;

  return (
    <div className="space-y-4" data-testid="roi-panel">
      {/* Hero — Portfolio classification */}
      <div className="bg-gradient-to-br from-emerald-950/40 to-zinc-900 border border-emerald-700/40 rounded-xl p-4">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Brain className="text-emerald-400" size={18}/>
              <h3 className="text-zinc-100 text-base font-bold">&ldquo;Is It Worth It?&rdquo; Portfolio Verdict</h3>
            </div>
            <p className="text-zinc-400 text-xs mt-0.5">
              Compares interest paid against gross profit during each loan&apos;s lifespan. Buffer = how much margin must exceed rate to be a clear &ldquo;Net Gain&rdquo;.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-zinc-500">Gain buffer:</span>
            {[2, 5, 10].map(b => (
              <button key={b} onClick={() => setBuffer(b)} data-testid={`roi-buf-${b}`}
                className={`px-2 py-0.5 rounded border text-[11px] ${buffer === b ? 'bg-emerald-500 text-black border-emerald-500 font-semibold' : 'bg-zinc-900 text-zinc-400 border-zinc-800'}`}>
                ±{b}%
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <SummaryCard label="Interest Paid" val={fmt(p.total_interest_paid)} testId="roi-int"/>
          <SummaryCard label="Gross Profit" val={fmt(p.total_gross_profit)} testId="roi-gp"/>
          <SummaryCard label="Burden Score" val={p.portfolio_burden_score == null ? 'n/a' : p.portfolio_burden_score} testId="roi-burden" hot={p.portfolio_burden_score > 0.5} />
          <ClassCard label="Net Gain"   val={p.by_classification['Net Gain']}      style={CLASS_STYLE['Net Gain']}/>
          <ClassCard label="Break-even" val={p.by_classification['Break-even']}    style={CLASS_STYLE['Break-even']}/>
          <ClassCard label="Burden"     val={p.by_classification['High-Cost Burden']} style={CLASS_STYLE['High-Cost Burden']}/>
        </div>
      </div>

      {/* D/E ratio + P&L impact side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {de && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="de-card">
            <div className="flex items-center gap-2 mb-2">
              <Scale className="text-amber-400" size={14}/>
              <h4 className="text-zinc-100 text-sm font-semibold">Debt-to-Equity Ratio</h4>
            </div>
            <div className="flex items-end gap-3">
              <div className="text-3xl font-bold text-zinc-100 font-mono">
                {de.ratio == null ? '—' : de.ratio.toFixed(2)}
              </div>
              <div className={`text-xs font-semibold ${HEALTH_STYLE[de.health] || 'text-zinc-500'}`}>{de.health}</div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <Mini label="Loan Outstanding" val={fmt(de.loan_outstanding)}/>
              <Mini label="OD Outstanding" val={fmt(de.od_outstanding)}/>
              <Mini label="Total Debt" val={fmt(de.total_debt)} color="text-red-300"/>
              <Mini label="Equity (proxy)" val={fmt(de.equity)} color="text-emerald-300"/>
            </div>
            <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
              Equity proxy = Net Profit (revenue − all expenses). Ratio ≤ 1 is healthy, ≤ 2 acceptable, ≤ 3 stretched, &gt; 3 risk.
            </p>
          </div>
        )}
        {pnl && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="pnl-impact-card">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="text-violet-400" size={14}/>
              <h4 className="text-zinc-100 text-sm font-semibold">P&amp;L Impact — Financial Expense</h4>
            </div>
            <div className="text-3xl font-bold text-violet-300 font-mono">{fmt(pnl.total_financial_expense)}</div>
            <div className="text-xs text-zinc-400 mt-1">subtracted from Net Profit on the P&L statement</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <Mini label="Loan Interest" val={fmt(pnl.loan_interest_paid)}/>
              <Mini label="OD Interest" val={fmt(pnl.od_interest_paid)}/>
            </div>
          </div>
        )}
      </div>

      {/* Per-loan ROI grid */}
      <DataGrid
        testId="roi-grid"
        title="Per-loan ROI"
        subtitle="Click any column to sort/filter/export — drill into a loan to fix issues"
        icon={<BarChart3 size={14} className="text-emerald-400"/>}
        rows={roi.rows}
        rowKey={(r) => r.loan_id}
        initialSort={{ key: 'net_gain_pct', dir: 'asc' }}
        exportName="loan-roi"
        emptyText="No loans yet — add one to see ROI analysis"
        columns={[
          { key: 'classification', label: 'Verdict', type: 'enum',
            accessor: r => r.classification,
            render: r => {
              const st = CLASS_STYLE[r.classification] || CLASS_STYLE['Break-even'];
              return <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${st.bg} ${st.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}/>{r.classification}
              </span>;
            } },
          { key: 'lender_name', label: 'Lender', type: 'text',
            accessor: r => r.lender_name,
            render: r => <div className="text-xs"><div className="text-zinc-100">{r.lender_name}</div><div className="text-[10px] text-zinc-500">{r.purpose}</div></div> },
          { key: 'principal', label: 'Principal', type: 'number', align: 'right',
            accessor: r => r.principal,
            render: r => <span className="font-mono text-zinc-300 text-xs">{fmt(r.principal)}</span> },
          { key: 'annualized_rate', label: 'Rate', type: 'number', align: 'right',
            accessor: r => r.annualized_rate,
            render: r => <span className="font-mono text-amber-300 text-xs">{r.annualized_rate}%</span> },
          { key: 'interest_paid', label: 'Int. Paid', type: 'number', align: 'right',
            accessor: r => r.interest_paid,
            render: r => <span className="font-mono text-violet-300 text-xs">{fmt(r.interest_paid)}</span> },
          { key: 'revenue_in_window', label: 'Revenue (window)', type: 'number', align: 'right',
            accessor: r => r.revenue_in_window,
            render: r => <span className="font-mono text-emerald-300 text-xs">{fmt(r.revenue_in_window)}</span> },
          { key: 'gross_profit', label: 'Gross Profit', type: 'number', align: 'right',
            accessor: r => r.gross_profit,
            render: r => <span className={`font-mono text-xs font-bold ${r.gross_profit < 0 ? 'text-red-400' : 'text-emerald-300'}`}>{fmt(r.gross_profit)}</span> },
          { key: 'margin_pct', label: 'Margin %', type: 'number', align: 'right',
            accessor: r => r.margin_pct,
            render: r => <span className="font-mono text-zinc-200 text-xs">{r.margin_pct}%</span> },
          { key: 'net_gain_pct', label: 'Net Gain %', type: 'number', align: 'right',
            accessor: r => r.net_gain_pct,
            render: r => {
              const cls = r.net_gain_pct >= buffer ? 'text-green-400' : r.net_gain_pct >= -buffer ? 'text-amber-400' : 'text-red-400';
              return <span className={`font-mono text-xs font-bold ${cls}`}>{r.net_gain_pct > 0 ? '+' : ''}{r.net_gain_pct}%</span>;
            } },
          { key: 'burden_score', label: 'Burden Score', type: 'number', align: 'right',
            accessor: r => r.burden_score ?? 0,
            render: r => r.burden_score == null ? <span className="text-zinc-700 text-xs">—</span>
              : <span className={`font-mono text-xs ${r.burden_score > 0.5 ? 'text-red-400' : 'text-zinc-300'}`}>{r.burden_score}</span> },
        ]}
      />
    </div>
  );
}

function ClassCard({ label, val, style }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${style.bg}`}>
      <div className={`text-[10px] uppercase tracking-wider ${style.text} flex items-center gap-1`}>
        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`}/>{label}
      </div>
      <div className={`text-base font-bold mt-0.5 ${style.text}`}>{val}</div>
    </div>
  );
}

function Mini({ label, val, color }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5">
      <div className="text-[9px] text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`text-xs font-mono ${color || 'text-zinc-200'}`}>{val}</div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// iter196 — SettlementModal: early-closure / foreclosure cost calculator.
//
// Operator flow:
//   1. Modal opens with TODAY auto-quoted (calls /settlement-quote).
//   2. Operator can tweak target_date (push it to a Sunday EMI date),
//      foreclosure_charge% (lender's policy — often 2-4%), and toggle GST.
//   3. The "Is it worth it?" verdict is computed live (interest_saved vs
//      foreclosure_charge + GST). Green = save money, red = don't bother.
//   4. Confirm → POST /settle → loan flips to Settled, single OUTFLOW row
//      lands on cashflow, payment history gets one settlement payment.
// ════════════════════════════════════════════════════════════════════════════
function SettlementModal({ loan, onClose, onSaved }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [targetDate, setTargetDate] = useState(today());
  const [chargePct, setChargePct] = useState(loan.foreclosure_charge_pct ?? 3.0);
  const [chargeFlat, setChargeFlat] = useState(0);
  const [gstOnCharge, setGstOnCharge] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('NEFT');
  const [notes, setNotes] = useState('');

  const fetchQuote = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/loans/${loan.id}/settlement-quote`, {
        params: {
          target_date: targetDate,
          foreclosure_charge_pct: chargePct,
          foreclosure_charge_flat: chargeFlat,
          gst_on_charge: gstOnCharge,
        },
        withCredentials: true,
        bypassCache: true,
      });
      setQuote(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to fetch settlement quote');
    } finally { setLoading(false); }
  }, [loan.id, targetDate, chargePct, chargeFlat, gstOnCharge]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const submit = async () => {
    if (!quote) return;
    if (!window.confirm(
      `Settle ${loan.lender_name} for ₹${quote.total_payable.toLocaleString('en-IN')}?\n\n` +
      `This marks the loan as FORECLOSED. ` +
      `A single outflow will be posted on the cashflow for the full settlement amount. ` +
      `This action can be reversed only by deleting the settlement payment from the loan detail view.`
    )) return;
    setBusy(true);
    try {
      await axios.post(`${API}/loans/${loan.id}/settle`, {
        total_payable:    quote.total_payable,
        principal_portion: quote.outstanding_principal,
        interest_portion:  quote.interest_to_date,
        foreclosure_charge: quote.foreclosure_charge,
        gst_amount:        quote.gst_amount,
        payment_date:      targetDate,
        payment_method:    paymentMethod,
        notes:             notes || `Foreclosure / settlement on ${targetDate}`,
      }, { withCredentials: true });
      toast.success('Loan settled successfully');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Settlement failed');
    } finally { setBusy(false); }
  };

  const verdictHot = quote && quote.net_savings <= 0;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="loan-settle-modal" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="text-zinc-100 font-bold text-sm flex items-center gap-2">
              <CheckCircle2 size={16} className="text-purple-400"/> Settle Loan — {loan.lender_name}
            </h3>
            <p className="text-zinc-500 text-[11px] mt-0.5">Early-closure / foreclosure cost calculator. Snapshot recomputes as you tweak the parameters below.</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid="loan-settle-close">
            <X size={16}/>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Parameters */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-3 space-y-3" data-testid="settle-params">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <SettleField label="Settle on date">
                <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)}
                  data-testid="settle-target-date"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 text-[12px] focus:border-amber-500 focus:outline-none"/>
              </SettleField>
              <SettleField label="Foreclosure %">
                <input type="number" step="any" min="0" max="20" value={chargePct} onChange={e => setChargePct(parseFloat(e.target.value) || 0)}
                  data-testid="settle-charge-pct"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 text-[12px] focus:border-amber-500 focus:outline-none"/>
              </SettleField>
              <SettleField label="Flat fee ₹">
                <input type="number" step="any" min="0" value={chargeFlat} onChange={e => setChargeFlat(parseFloat(e.target.value) || 0)}
                  data-testid="settle-charge-flat"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 text-[12px] focus:border-amber-500 focus:outline-none"/>
              </SettleField>
            </div>
            <label className="flex items-center gap-2 text-[11px] text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={gstOnCharge} onChange={e => setGstOnCharge(e.target.checked)} data-testid="settle-gst-toggle"/>
              GST @18% on foreclosure charge (most banks levy this)
            </label>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10 text-zinc-500 text-sm">
              <Clock size={14} className="animate-spin mr-2"/> Computing quote…
            </div>
          ) : quote ? (
            <>
              {/* Cost breakdown */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden" data-testid="settle-quote-breakdown">
                <div className="px-3 py-2 bg-zinc-900 text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Settlement Quote — Today&apos;s Cost</div>
                <table className="w-full text-[12px]">
                  <tbody>
                    <SettleRow label="Outstanding Principal"    value={quote.outstanding_principal} accent />
                    <SettleRow label="Accrued Interest (till date)" value={quote.interest_to_date} accent />
                    <SettleRow label={`Foreclosure Charge (${quote.foreclosure_charge_pct}% or flat ₹${quote.foreclosure_charge_flat})`} value={quote.foreclosure_charge} />
                    {quote.gst_amount > 0 && <SettleRow label="GST @18%" value={quote.gst_amount} dim />}
                    <tr className="border-t-2 border-amber-700/60 bg-amber-950/20">
                      <td className="px-3 py-2.5 text-amber-200 font-semibold">Total Payable Today</td>
                      <td className="px-3 py-2.5 text-right font-mono text-amber-200 font-bold text-base" data-testid="settle-total">{fmt(quote.total_payable)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Verdict */}
              <div className={`rounded-xl border p-3 ${verdictHot ? 'border-red-700/60 bg-red-950/20' : 'border-emerald-700/40 bg-emerald-950/15'}`}
                   data-testid="settle-verdict">
                <div className="flex items-start gap-3">
                  {verdictHot
                    ? <AlertTriangle size={18} className="text-red-300 mt-0.5"/>
                    : <CheckCircle2 size={18} className="text-emerald-300 mt-0.5"/>}
                  <div className="flex-1">
                    <div className={`text-sm font-semibold ${verdictHot ? 'text-red-200' : 'text-emerald-200'}`}>
                      {quote.recommendation}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                      <Mini label="If not closed" val={fmt(quote.if_not_closed_interest)} color="text-zinc-300"/>
                      <Mini label="Interest saved" val={fmt(quote.interest_saved)} color="text-emerald-300"/>
                      <Mini label="Cost of closing" val={fmt(quote.foreclosure_charge + quote.gst_amount)} color="text-amber-300"/>
                      <Mini label="Net savings"
                        val={(quote.net_savings >= 0 ? '+' : '−') + fmt(Math.abs(quote.net_savings))}
                        color={verdictHot ? 'text-red-300' : 'text-emerald-300'}/>
                    </div>
                    {quote.months_remaining > 0 && (
                      <div className="mt-2 text-[10px] text-zinc-500">
                        Tenure ends {quote.tenure_end_date} · {quote.months_remaining} months remaining
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Settlement payment input */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-3 space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <SettleField label="Payment method">
                    <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                      data-testid="settle-method"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 text-[12px] focus:border-amber-500 focus:outline-none">
                      <option>NEFT</option><option>RTGS</option><option>IMPS</option>
                      <option>UPI</option><option>Cheque</option><option>Cash</option>
                    </select>
                  </SettleField>
                  <SettleField label="Reference / notes">
                    <input value={notes} onChange={e => setNotes(e.target.value)}
                      data-testid="settle-notes"
                      placeholder="e.g. NEFT ref UTR1234, foreclosure letter dated…"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 text-[12px] focus:border-amber-500 focus:outline-none"/>
                  </SettleField>
                </div>
              </div>
            </>
          ) : null}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-zinc-400 hover:text-zinc-100 text-xs">Cancel</button>
            <button onClick={submit} disabled={busy || !quote || loading}
              data-testid="settle-confirm"
              className="px-5 py-2 bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold rounded-md disabled:opacity-50">
              {busy ? 'Settling…' : `Settle for ${quote ? fmt(quote.total_payable) : '—'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettleRow({ label, value, accent, dim }) {
  return (
    <tr className="border-b border-zinc-800/50">
      <td className={`px-3 py-2 ${dim ? 'text-zinc-500' : 'text-zinc-300'}`}>{label}</td>
      <td className={`px-3 py-2 text-right font-mono ${accent ? 'text-zinc-100' : dim ? 'text-zinc-400' : 'text-zinc-200'}`}>{fmt(value)}</td>
    </tr>
  );
}

function SettleField({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  );
}
